import { useEffect, useState, Fragment } from "react";
import { flushSync } from "react-dom";
import { VISOR_MODULE_BREADCRUMB_ROOT } from "@/modules/workModuleCore";
import { useNavigate } from "react-router-dom";
import api, { fmtMoney, fmtNumber, formatApiError, API } from "@/lib/api";
import { usePersistedClientId } from "@/lib/clientSelection";
import { PageHeader } from "./layoutChrome";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import DateFieldMx from "@/components/DateFieldMx";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { CalendarRange, FileSpreadsheet, FileSearch, Loader2, Users, Eye, ChevronDown, AlertTriangle, CheckCircle2, ArrowRightLeft, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { goToSatVisorForUuid } from "@/lib/satVisorNavigation";
import { confrontaHasDifferences, MesSinDiferenciasAviso, ExcelConfrontaDiagnostico } from "@/lib/confrontaDiff";
import { normalizeConfrontaResponse } from "@/lib/confrontaNormalize";
import * as CF from "@/lib/confrontaLabels";
import { cn } from "@/lib/utils";
import { satExpandChevronCn, satExpandMainCn, satExpandRowCn, satExpandToggleCn } from "@/lib/expandableSatRail";
import { SatExpandTableRailCell } from "@/components/SatExpandTableRailCell";
import ConfrontaDiferenciasBundleDialog from "@/components/confronta/ConfrontaDiferenciasBundleDialog";
import ConfrontaRunProgress from "@/components/confronta/ConfrontaRunProgress";
import { yieldToUi } from "@/lib/yieldToUi";

const MES_FULL = ["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MES_LBL = ["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"];

const splitDate = (s) => { const [y,m,d] = (s||"").split("-").map(n=>parseInt(n,10)); return [y,m,d]; };
const monthsInRange = (a,b) => { const [yA,mA]=splitDate(a); const [yB,mB]=splitDate(b); if (!yA||!mA||!yB||!mB) return 0; return (yB-yA)*12+(mB-mA)+1; };

export default function ConfrontaNominas({ embedMode = false }) {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = usePersistedClientId(clients);
  const [year, setYear] = useState(() => {
    const saved = Number(localStorage.getItem("vf:selected_year") || 0);
    if (saved >= 2020 && saved <= 2099) return saved;
    const now = new Date();
    return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
  });
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [confronta, setConfronta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [runProgress, setRunProgress] = useState(null);
  const [openMonths, setOpenMonths] = useState({});
  const [openWorkers, setOpenWorkers] = useState({});
  const [nominaCriteriaOpen, setNominaCriteriaOpen] = useState(false);
  const [bundleDialogOpen, setBundleDialogOpen] = useState(false);

  const selectedClient = clients.find((c) => String(c.id) === String(clientId)) || null;

  useEffect(() => {
    api.get("/clients")
      .then(({ data }) => setClients(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error(err);
        setClients([]);
        toast.error("Error al cargar clientes");
      });
  }, []);

  useEffect(() => {
    setFechaDesde(`${year}-01-01`);
    setFechaHasta(`${year}-12-31`);
    try { localStorage.setItem("vf:selected_year", String(year)); } catch { /* ignore */ }
  }, [year]);

  const buildMonthList = (a, b) => {
    const [yA,mA,dA] = splitDate(a); const [yB,mB,dB] = splitDate(b);
    const out = [];
    let y = yA, m = mA;
    while (y < yB || (y === yB && m <= mB)) {
      const monthFirst = new Date(y, m-1, 1);
      const monthLast = new Date(y, m, 0);
      const fromD = (y === yA && m === mA) ? new Date(yA, mA-1, dA) : monthFirst;
      const toD = (y === yB && m === mB) ? new Date(yB, mB-1, dB) : monthLast;
      out.push({ key: `${y}-${String(m).padStart(2,'0')}`, label: `${MES_FULL[m-1]} ${y}`, from: fromD.toISOString().slice(0,10), to: toD.toISOString().slice(0,10) });
      m++; if (m > 12) { m = 1; y++; }
    }
    return out;
  };

  const runConfronta = async () => {
    if (!fechaDesde || !fechaHasta) { toast.error("Selecciona el periodo"); return; }
    if (fechaDesde > fechaHasta) { toast.error("La fecha 'desde' no puede ser mayor a 'hasta'"); return; }
    flushSync(() => {
      setBusy(true);
      setOpenMonths({});
      setOpenWorkers({});
      setRunProgress({ percent: 5, message: "Iniciando confronta nóminas…", current: 0, total: 1 });
    });
    await yieldToUi();
    try {
      const total = monthsInRange(fechaDesde, fechaHasta);
      if (total <= 1) {
        flushSync(() => {
          setRunProgress({ percent: 25, message: "Cruzando nóminas SAT con Excel…", current: 1, total: 1 });
        });
        await yieldToUi();
        const { data } = await api.get("/confronta/nominas", { params: { client_id: clientId, fecha_desde: fechaDesde, fecha_hasta: fechaHasta }});
        setRunProgress({ percent: 95, message: "Preparando resultados…", current: 1, total: 1 });
        setConfronta({ mode: "single", data: normalizeConfrontaResponse(data, "nominas") });
      } else {
        const months = buildMonthList(fechaDesde, fechaHasta);
        const monthly = [];
        for (let i = 0; i < months.length; i++) {
          const m = months[i];
          const pct = Math.round((i / months.length) * 100);
          flushSync(() => {
            setRunProgress({
              percent: Math.max(pct, 3),
              message: `Cruzando ${m.label}…`,
              current: i + 1,
              total: months.length,
            });
          });
          await yieldToUi();
          const { data } = await api.get("/confronta/nominas", { params: { client_id: clientId, fecha_desde: m.from, fecha_hasta: m.to }});
          monthly.push({ ...m, data: normalizeConfrontaResponse(data, "nominas") });
          setRunProgress({
            percent: Math.round(((i + 1) / months.length) * 100),
            message: `Mes ${m.label} listo`,
            current: i + 1,
            total: months.length,
          });
        }
        setConfronta({ mode: "multi", monthly });
      }
      setRunProgress({ percent: 100, message: "Confronta nóminas completada", current: total, total });
      toast.success(`Confronta nóminas ejecutada para ${fechaDesde} → ${fechaHasta}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error en confronta");
    } finally { setBusy(false); setRunProgress(null); }
  };

  const openInSatVisor = async (uuid) => {
    const u = (uuid || "").trim();
    if (!u) return;
    try {
      await goToSatVisorForUuid(navigate, clientId, u);
    } catch (e) {
      const st = e?.response?.status;
      if (st === 404) {
        toast.error("No hay XML de nómina cargado para este UUID.");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || e.message || "No se pudo abrir el visor");
      }
    }
  };

  const dlMismoMesFechas = async () => {
    if (!fechaDesde || !fechaHasta) return;
    try {
      const { data } = await api.get("/reports/confronta-mismo-mes-fechas-excel", {
        responseType: "blob",
        params: { client_id: clientId, fecha_desde: fechaDesde, fecha_hasta: fechaHasta, direccion: "nomina" },
      });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Confronta_mismo_mes_fechas_nomina_${(fechaDesde || "").replaceAll("-", "")}_a_${(fechaHasta || "").replaceAll("-", "")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 300000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "No se pudo descargar el reporte de fechas");
    }
  };

  const years = [year+1, year, year-1, year-2, year-3, year-4];

  const headerRight = (
          <div className="flex items-center gap-2">
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-56 h-9 rounded-sm" data-testid="nom-client"><SelectValue placeholder="Cliente"/></SelectTrigger>
              <SelectContent>{clients.map(c => <SelectItem key={c.id} value={c.id}>{c.razon_social} — {c.rfc}{c.owner_email ? `  ·  ${c.owner_email}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v)=>setYear(parseInt(v))}>
              <SelectTrigger className="w-24 h-9 rounded-sm"><SelectValue/></SelectTrigger>
              <SelectContent>{years.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        );

  return (
    <>
      {embedMode ? (
        <div className="flex flex-wrap items-center justify-end gap-2 mb-4">{headerRight}</div>
      ) : (
      <PageHeader
        title={CF.TITLE_CONFRONTA_NOMINA}
        subtitle="Cruza CFDI de Nómina (tipo N) emitidos por el cliente (sistema SAT) contra el control de IVA en Excel (Contabilidad). Desglosa ISR retenido por trabajador."
        breadcrumb={[VISOR_MODULE_BREADCRUMB_ROOT, "Confronta Nóminas"]}
        right={headerRight}
      />
      )}
      <div className={embedMode ? "space-y-6" : "p-8 space-y-6"}>
        {!clients.length ? (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-10 text-center">
            <div className="text-sm text-slate-500">Registra primero un cliente.</div>
          </div>
        ) : (
        <>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <Label className="text-[11px] text-slate-600 flex items-center gap-1"><CalendarRange className="w-3 h-3"/> Desde</Label>
                <DateFieldMx
                  value={fechaDesde}
                  onChange={setFechaDesde}
                  className="rounded-sm h-9 w-[150px] mt-1"
                  minIso="2000-01-01"
                  maxIso="2100-12-31"
                  data-testid="nom-fecha-desde"
                />
              </div>
              <div>
                <Label className="text-[11px] text-slate-600 flex items-center gap-1"><CalendarRange className="w-3 h-3"/> Hasta</Label>
                <DateFieldMx
                  value={fechaHasta}
                  onChange={setFechaHasta}
                  className="rounded-sm h-9 w-[150px] mt-1"
                  minIso="2000-01-01"
                  maxIso="2100-12-31"
                  data-testid="nom-fecha-hasta"
                />
              </div>
              <Button onClick={runConfronta} disabled={busy} className="h-9 rounded-sm bg-slate-900 hover:bg-slate-800 text-xs" data-testid="run-nom-btn">
                {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin"/> : null}
                Ejecutar confronta
              </Button>
              {confronta && (
                <>
                <Button
                  variant="outline"
                  className="h-9 rounded-sm text-xs border-rose-300 text-rose-700 hover:bg-rose-50"
                  onClick={() => setBundleDialogOpen(true)}
                  data-testid="nom-diferencias-bundle-btn"
                >
                  <FileSearch className="w-3.5 h-3.5 mr-1.5"/> Generar diferencias y envío (PDF + Excel)
                </Button>
                <Button
                  variant="outline"
                  className="h-9 rounded-sm text-xs border-[#3DA873] text-[#3DA873] hover:bg-[#3DA873]/10"
                  onClick={async () => {
                    try {
                      const r = await api.get("/reports/confronta-nominas-excel", {
                        params: { client_id: clientId, fecha_desde: fechaDesde, fecha_hasta: fechaHasta },
                        responseType: "blob",
                      });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement("a");
                      a.href = url;
                      const cli = clients.find(c => c.id === clientId);
                      a.download = `ConfrontaNominas_${cli?.razon_social || "cliente"}_${fechaDesde}_${fechaHasta}.xlsx`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch { toast.error("No se pudo generar el Excel"); }
                  }}
                  data-testid="nom-excel-btn"
                >
                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5"/> {CF.DESCARGAR_EXCEL_CONTAB}
                </Button>
                </>
              )}
              <span className="text-[11px] text-slate-500 ml-2">
                Carga el Excel (Contabilidad) de Nómina desde la pestaña <b>Confronta Egresos</b> (mismo botón).
              </span>
            </div>
            {busy && runProgress ? (
              <ConfrontaRunProgress progress={runProgress} />
            ) : null}
          </div>

          <section className="border border-slate-200 bg-white rounded-sm overflow-hidden text-[11px] text-slate-700">
            <button
              type="button"
              className={cn(satExpandRowCn(), "w-full text-left outline-none")}
              onClick={() => setNominaCriteriaOpen((v) => !v)}
              aria-expanded={nominaCriteriaOpen}
              data-testid="nomina-criteria-toggle"
            >
              <span className={cn(satExpandMainCn(nominaCriteriaOpen), "text-[11px] font-semibold text-slate-800")}>
                Criterios de confronta de nóminas y enlace al visor
              </span>
              <span className={satExpandToggleCn(nominaCriteriaOpen)} aria-hidden>
                <ChevronDown className={satExpandChevronCn(nominaCriteriaOpen)} />
              </span>
            </button>
            {nominaCriteriaOpen && (
            <div className="px-4 pb-3 pt-0 border-t border-slate-100 space-y-2 text-slate-600">
              <p>
                Se comparan recibos de nómina <strong>PUE</strong> y liquidaciones <strong>PPD</strong> con complementos de pago en el periodo
                según la <strong>fecha de pago</strong>. El ícono de enlace abre el XML en el visor de nóminas (copia local, no consulta en vivo al SAT).
              </p>
              <p>
                Si el complemento de nómina 1.2 no está extraído en un XML antiguo, el ISR del sistema (SAT) puede aparecer en cero hasta re-subir el archivo.
              </p>
            </div>
            )}
          </section>

          {confronta?.mode === "single" && (
            <NominaDetail
              data={confronta.data}
              openWorkers={openWorkers}
              setOpenWorkers={setOpenWorkers}
              onOpenSatVisor={openInSatVisor}
            />
          )}
          {confronta?.mode === "multi" && (
            <MultiMonthNomina
              monthly={confronta.monthly}
              openMonths={openMonths}
              setOpenMonths={setOpenMonths}
              openWorkers={openWorkers}
              setOpenWorkers={setOpenWorkers}
              onOpenSatVisor={openInSatVisor}
            />
          )}
        </>
        )}
      </div>

      <ConfrontaDiferenciasBundleDialog
        open={bundleDialogOpen}
        onOpenChange={setBundleDialogOpen}
        clientId={clientId}
        client={selectedClient}
        fechaDesde={fechaDesde}
        fechaHasta={fechaHasta}
        direccion="nomina"
      />
    </>
  );
}

function NominaDetail({ data: rawData, openWorkers, setOpenWorkers, embedded, onOpenSatVisor }) {
  const data = normalizeConfrontaResponse(rawData, "nominas");
  const t = data.totales;
  const s = data.summary;
  const toggleWorker = (rfc) => setOpenWorkers(prev => ({ ...prev, [rfc]: !prev[rfc] }));
  const detalleIncompleto = data.matched.concat(data.mismatched).some(r => Math.abs(r.isr_sistema) < 0.01 && Math.abs(r.isr_excel) > 0.01);
  const sinDiferencias = !confrontaHasDifferences(data);
  const excelEnPeriodo =
    data.excel_meta?.rows_en_periodo ?? data.totales?.excel?.count ?? null;
  const avisoSinExcel =
    excelEnPeriodo === 0 &&
    ((data.summary?.solo_en_sistema || 0) > 0 || (data.summary?.mismatched || 0) > 0);

  return (
    <div className="space-y-4" data-testid="nomina-detail">
      <ExcelConfrontaDiagnostico data={data} />
      {!embedded && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <KpiCard label="Trabajadores" value={fmtNumber(s.total_workers)} color="blue" icon={Users}/>
            <KpiCard label="Coincidentes" value={fmtNumber(s.matched)} color="emerald" icon={CheckCircle2}/>
            <KpiCard label="Con diferencias" value={fmtNumber(s.mismatched)} color="amber" icon={AlertTriangle}/>
            <KpiCard label={CF.STAT_SOLO_EXCEL} value={fmtNumber(s.solo_en_excel)} color="red" icon={ArrowRightLeft}/>
            <KpiCard label={CF.STAT_SOLO_SISTEMA} value={fmtNumber(s.solo_en_sistema)} color="rose" icon={ArrowRightLeft}/>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
            <div className="grid grid-cols-3 text-xs">
              <ColTotals title={CF.CFDI_EN_SISTEMA_SAT} perc={t.sistema.percepciones} isr={t.sistema.isr} neto={t.sistema.neto} count={t.sistema.count}/>
              <ColTotals title={CF.EXCEL_CONTABILIDAD} perc={t.excel.percepciones} isr={t.excel.isr} neto={t.excel.neto} count={t.excel.count}/>
              <ColTotals title="Diferencia" perc={t.diff.percepciones} isr={t.diff.isr} neto={t.diff.neto} highlight/>
            </div>
          </div>

          {sinDiferencias && (
            <div
              className="bg-emerald-50 border border-emerald-200 rounded-sm p-3 text-xs text-emerald-950"
              data-testid="confronta-nomina-sin-diferencias-detalle"
            >
              <b>No hay diferencias en este periodo.</b> Los recibos del sistema (SAT) y la contabilidad están alineados.
            </div>
          )}

          {avisoSinExcel && (
            <div
              className="bg-red-50 border border-red-200 rounded-sm p-3 text-xs text-red-950"
              data-testid="confronta-nomina-sin-excel-periodo"
            >
              <b>Sin filas de Excel (Contabilidad) de nómina en este periodo.</b> No hay registros con fecha de
              ingreso o documento entre {data.fecha_desde} y {data.fecha_hasta}. Cargue el Excel, CSV o use conciliación
              manual en{" "}
              <a href="/carga-informacion" className="underline font-semibold">
                Carga de información
              </a>{" "}
              y revise que la fecha de pago del Excel coincida con el mes confrontado.
            </div>
          )}

          {detalleIncompleto && (
            <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900">
              <b>Aviso:</b> Algunos CFDI de Nómina cargados en el sistema (SAT) antes de esta versión no tienen el complemento de Nómina 1.2 procesado. Por eso el ISR del sistema (SAT) aparece en cero. Para ver el desglose completo (ISR por trabajador, fecha de pago, percepciones gravadas/exentas), <b>re-sube los XMLs de Nómina</b> desde el módulo Visor CFDI.
            </div>
          )}
        </>
      )}

      <Tabs defaultValue="workers">
        <TabsList className="rounded-sm bg-slate-100">
          <TabsTrigger value="workers" className="rounded-sm" data-testid="tab-workers">Por trabajador ({s.total_workers})</TabsTrigger>
          <TabsTrigger value="recibos" className="rounded-sm" data-testid="tab-recibos">Recibos del periodo ({t.sistema.count})</TabsTrigger>
          <TabsTrigger value="mismatched" className="rounded-sm" data-testid="tab-nom-diff">Con diferencias ({s.mismatched})</TabsTrigger>
          <TabsTrigger value="only_excel" className="rounded-sm">{CF.TAB_SOLO_EXCEL} ({s.solo_en_excel})</TabsTrigger>
          <TabsTrigger value="only_sis" className="rounded-sm">{CF.TAB_SOLO_SISTEMA} ({s.solo_en_sistema})</TabsTrigger>
        </TabsList>

        <TabsContent value="workers">
          <div className="border border-slate-200 rounded-sm overflow-hidden mt-3">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">RFC</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Trabajador</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Puesto / Depto</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Recibos</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Percepciones</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Gravado</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Exento</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">ISR retenido</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">IMSS</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Neto pagado</th>
                  <th className="w-10 border-l border-slate-200 bg-slate-50 p-0" aria-hidden />
                </tr>
              </thead>
              <tbody>
                {data.workers.map((w) => {
                  const isOpen = !!openWorkers[w.rfc];
                  const recibos = data.matched.concat(data.mismatched).concat(data.solo_en_sistema).filter(r => r.rfc_trabajador === w.rfc);
                  return (
                    <Fragment key={w.rfc}>
                      <tr className="border-t border-slate-100 cursor-pointer hover:bg-slate-50" onClick={() => toggleWorker(w.rfc)}>
                        <td className="px-3 py-1.5 font-mono text-[10px]">{w.rfc}</td>
                        <td className="px-3 py-1.5 font-medium text-slate-800">{w.nombre}</td>
                        <td className="px-3 py-1.5 text-slate-500">{[w.puesto, w.departamento].filter(Boolean).join(" / ") || "—"}</td>
                        <td className="px-3 py-1.5 text-right num">{w.num_recibos}</td>
                        <td className="px-3 py-1.5 text-right num">{fmtMoney(w.total_percepciones)}</td>
                        <td className="px-3 py-1.5 text-right num text-slate-500">{fmtMoney(w.total_gravado)}</td>
                        <td className="px-3 py-1.5 text-right num text-slate-500">{fmtMoney(w.total_exento)}</td>
                        <td className="px-3 py-1.5 text-right num text-amber-700 font-semibold">{fmtMoney(w.isr_retenido)}</td>
                        <td className="px-3 py-1.5 text-right num text-slate-500">{fmtMoney(w.imss_retenido)}</td>
                        <td className="px-3 py-1.5 text-right num font-semibold">{fmtMoney(w.total_neto)}</td>
                        <SatExpandTableRailCell open={isOpen} />
                      </tr>
                      {isOpen && recibos.length > 0 && (
                        <tr className="bg-slate-50/60">
                          <td colSpan={11} className="p-3">
                            <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                              <div className="px-3 py-1.5 bg-slate-50 text-[10px] uppercase tracking-wider font-semibold text-slate-500 border-b border-slate-100">Recibos de {w.nombre}</div>
                              <table className="w-full text-xs">
                                <thead>
                                  <tr className="border-b border-slate-100">
                                    <th className="text-left px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">Fecha pago</th>
                                    <th className="text-left px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">UUID</th>
                                    <th className="text-left px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">Folio</th>
                                    <th className="text-right px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">Percepciones</th>
                                    <th className="text-right px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">ISR sistema (SAT)</th>
                                    <th className="text-right px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">ISR Excel (Contabilidad)</th>
                                    <th className="text-right px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">Δ ISR</th>
                                    <th className="text-right px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">Neto CFDI (SAT)</th>
                                    <th className="text-right px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">Pago Excel (Contabilidad)</th>
                                    <th className="text-right px-3 py-1 text-[10px] uppercase text-slate-500 font-semibold">Δ Neto</th>
                                    <th className="text-center px-2 py-1 text-[10px] uppercase text-slate-500 font-semibold w-10">Visor</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {recibos.map((r) => {
                                    const sisIsr = r.isr_sistema ?? r.isr_retenido ?? 0;
                                    const excelIsr = r.isr_excel ?? 0;
                                    const sisNeto = r.total_sistema ?? r.total ?? 0;
                                    const excelNeto = r.total_excel ?? 0;
                                    const diff_isr = r.diff_isr ?? (sisIsr - excelIsr);
                                    const diff_neto = r.diff_total ?? (sisNeto - excelNeto);
                                    return (
                                      <tr key={r.uuid} className="border-t border-slate-50">
                                        <td className="px-3 py-1 font-mono text-[10px]">{r.fecha_pago || "—"}</td>
                                        <td className="px-3 py-1 font-mono text-[10px]">{(r.uuid||"").slice(0,8)}…</td>
                                        <td className="px-3 py-1 font-mono">{r.folio || "—"}</td>
                                        <td className="px-3 py-1 text-right num">{fmtMoney(r.total_percepciones_sistema || r.total_percepciones || 0)}</td>
                                        <td className="px-3 py-1 text-right num text-amber-700">{fmtMoney(sisIsr)}</td>
                                        <td className="px-3 py-1 text-right num text-slate-500">{fmtMoney(excelIsr)}</td>
                                        <td className={`px-3 py-1 text-right num ${Math.abs(diff_isr) > 0.01 ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{fmtMoney(diff_isr)}</td>
                                        <td className="px-3 py-1 text-right num font-semibold">{fmtMoney(sisNeto)}</td>
                                        <td className="px-3 py-1 text-right num text-slate-500">{fmtMoney(excelNeto)}</td>
                                        <td className={`px-3 py-1 text-right num ${Math.abs(diff_neto) > 0.01 ? "text-amber-700 font-semibold" : "text-slate-400"}`}>{fmtMoney(diff_neto)}</td>
                                        <td className="px-2 py-1 text-center">
                                          {r.uuid && onOpenSatVisor ? (
                                            <button
                                              type="button"
                                              className="p-0.5 rounded-sm text-[#0F2D5C] hover:bg-slate-100 inline-flex"
                                              title="Abrir en visor SAT (nóminas)"
                                              onClick={() => onOpenSatVisor(r.uuid)}
                                            >
                                              <ExternalLink className="w-3.5 h-3.5"/>
                                            </button>
                                          ) : (
                                            <span className="text-slate-300">—</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
              <tfoot className="bg-slate-100 border-t-2 border-slate-300">
                <tr>
                  <td colSpan={3} className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-700">TOTAL</td>
                  <td className="px-3 py-2 text-right num font-bold">{fmtNumber(t.sistema.count)}</td>
                  <td className="px-3 py-2 text-right num font-bold">{fmtMoney(t.sistema.percepciones)}</td>
                  <td className="px-3 py-2 text-right num text-slate-600">—</td>
                  <td className="px-3 py-2 text-right num text-slate-600">—</td>
                  <td className="px-3 py-2 text-right num font-bold text-amber-700">{fmtMoney(t.sistema.isr)}</td>
                  <td className="px-3 py-2 text-right num text-slate-600">—</td>
                  <td className="px-3 py-2 text-right num font-bold">{fmtMoney(t.sistema.neto)}</td>
                  <td className="w-10 border-l border-slate-300 bg-slate-100 p-0" aria-hidden />
                </tr>
              </tfoot>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="recibos">
          <SimpleTable rows={data.matched.concat(data.mismatched)} onOpenSatVisor={onOpenSatVisor} cols={[
            {k:"fpago", label:"Fecha pago", render:r=>r.fecha_pago||"—"},
            {k:"uuid",  label:"UUID", mono:true, render:r=>(r.uuid||"").slice(0,8)+"…"},
            {k:"rfc",   label:"RFC", mono:true, render:r=>r.rfc_trabajador},
            {k:"nom",   label:"Trabajador", render:r=>r.nombre_trabajador},
            {k:"folio", label:"Folio", render:r=>r.folio||"—"},
            {k:"perc",  label:"Percepciones", right:true, render:r=>fmtMoney(r.total_percepciones_sistema)},
            {k:"isrs",  label:"ISR sistema (SAT)",   right:true, render:r=><span className="text-amber-700">{fmtMoney(r.isr_sistema)}</span>},
            {k:"isre",  label:"ISR Excel (Contabilidad)", right:true, render:r=>fmtMoney(r.isr_excel)},
            {k:"neto",  label:"Neto pagado", right:true, render:r=>fmtMoney(r.total_sistema)},
            {k:"diff",  label:"Δ",   right:true, render:r=>{ const d=r.diff_total||0; return <span className={Math.abs(d)>0.01?"text-amber-700 font-semibold":"text-slate-400"}>{fmtMoney(d)}</span>}},
          ]}/>
        </TabsContent>

        <TabsContent value="mismatched">
          <p className="text-xs text-slate-500 px-2 py-2">Mismo UUID en sistema (SAT) y Excel (Contabilidad) pero el ISR retenido o el neto pagado no coinciden.</p>
          <SimpleTable rows={data.mismatched} onOpenSatVisor={onOpenSatVisor} cols={[
            {k:"fpago", label:"Fecha pago", render:r=>r.fecha_pago||"—"},
            {k:"uuid",  label:"UUID", mono:true, render:r=>(r.uuid||"").slice(0,8)+"…"},
            {k:"nom",   label:"Trabajador", render:r=>r.nombre_trabajador},
            {k:"folio", label:"Folio", render:r=>r.folio||"—"},
            {k:"perc_s", label:"Perc sistema (SAT)", right:true, render:r=>fmtMoney(r.total_percepciones_sistema)},
            {k:"perc_e", label:"Perc Excel (Contabilidad)", right:true, render:r=>fmtMoney(r.total_percepciones_excel)},
            {k:"diff_p", label:"Δ Perc", right:true, render:r=><span className="text-amber-700 font-semibold">{fmtMoney(r.diff_perc)}</span>},
            {k:"isr_s", label:"ISR sistema (SAT)", right:true, render:r=>fmtMoney(r.isr_sistema)},
            {k:"isr_e", label:"ISR Excel (Contabilidad)", right:true, render:r=>fmtMoney(r.isr_excel)},
            {k:"diff_i", label:"Δ ISR", right:true, render:r=><span className="text-amber-700 font-semibold">{fmtMoney(r.diff_isr)}</span>},
          ]}/>
        </TabsContent>

        <TabsContent value="only_excel">
          <p className="text-xs text-slate-500 px-2 py-2">Filas de nómina del Excel (Contabilidad) que NO tienen CFDI de Nómina cargado en el sistema (SAT).</p>
          <SimpleTable rows={data.solo_en_excel} onOpenSatVisor={onOpenSatVisor} cols={[
            {k:"fexcel", label: CF.FEC_EXCEL_CONTAB, render:r=>r.fecha_excel},
            {k:"uuid",  label:"UUID", mono:true, render:r=>r.uuid?(r.uuid.slice(0,8)+"…"):<span className="text-slate-400">(sin UUID)</span>},
            {k:"rfc",   label:"RFC", mono:true, render:r=>r.rfc_trabajador},
            {k:"nom",   label:"Trabajador", render:r=>r.nombre_trabajador},
            {k:"folio", label:"Folio", render:r=>r.folio||"—"},
            {k:"perc",  label:"Percepciones", right:true, render:r=>fmtMoney(r.total_percepciones_excel)},
            {k:"isr",   label:"ISR", right:true, render:r=>fmtMoney(r.isr_excel)},
            {k:"neto",  label:"Neto", right:true, render:r=>fmtMoney(r.total_excel)},
          ]}/>
        </TabsContent>

        <TabsContent value="only_sis">
          <p className="text-xs text-slate-500 px-2 py-2">CFDI de Nómina emitidos en el sistema (SAT) que NO aparecen en el Excel (Contabilidad) del control de IVA.</p>
          <SimpleTable rows={data.solo_en_sistema} onOpenSatVisor={onOpenSatVisor} cols={[
            {k:"fpago", label:"Fecha pago", render:r=>r.fecha_pago||"—"},
            {k:"uuid",  label:"UUID", mono:true, render:r=>(r.uuid||"").slice(0,8)+"…"},
            {k:"rfc",   label:"RFC", mono:true, render:r=>r.rfc_trabajador},
            {k:"nom",   label:"Trabajador", render:r=>r.nombre_trabajador},
            {k:"folio", label:"Folio", render:r=>r.folio||"—"},
            {k:"perc",  label:"Percepciones", right:true, render:r=>fmtMoney(r.total_percepciones)},
            {k:"isr",   label:"ISR", right:true, render:r=><span className="text-amber-700">{fmtMoney(r.isr_retenido)}</span>},
            {k:"neto",  label:"Neto", right:true, render:r=>fmtMoney(r.total)},
          ]}/>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MultiMonthNomina({ monthly, openMonths, setOpenMonths, openWorkers, setOpenWorkers, onOpenSatVisor }) {
  const allMonthsBalanced =
    monthly.length > 0 && monthly.every((blk) => !confrontaHasDifferences(blk.data));

  const agg = monthly.reduce((acc, blk) => {
    const t = blk.data.totales;
    acc.sistema.percepciones += t.sistema.percepciones;
    acc.sistema.isr += t.sistema.isr;
    acc.sistema.neto += t.sistema.neto;
    acc.sistema.count += t.sistema.count;
    acc.excel.percepciones += t.excel.percepciones;
    acc.excel.isr += t.excel.isr;
    acc.excel.neto += t.excel.neto;
    acc.excel.count += t.excel.count;
    acc.diff.percepciones += t.diff.percepciones;
    acc.diff.isr += t.diff.isr;
    acc.diff.neto += t.diff.neto;
    return acc;
  }, {
    sistema: {percepciones:0, isr:0, neto:0, count:0},
    excel:   {percepciones:0, isr:0, neto:0, count:0},
    diff:    {percepciones:0, isr:0, neto:0},
  });

  const toggleMonth = (k) => setOpenMonths(prev => ({ ...prev, [k]: !prev[k] }));

  return (
    <div className="space-y-4">
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="grid grid-cols-3 text-xs">
          <ColTotals title={CF.CFDI_EN_SISTEMA_SAT} perc={agg.sistema.percepciones} isr={agg.sistema.isr} neto={agg.sistema.neto} count={agg.sistema.count}/>
          <ColTotals title={CF.EXCEL_CONTABILIDAD} perc={agg.excel.percepciones} isr={agg.excel.isr} neto={agg.excel.neto} count={agg.excel.count}/>
          <ColTotals title="Diferencia" perc={agg.diff.percepciones} isr={agg.diff.isr} neto={agg.diff.neto} highlight/>
        </div>
      </div>

      {allMonthsBalanced && (
        <div
          className="rounded-sm border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950"
          data-testid="confronta-nomina-sin-diferencias-periodo"
        >
          <b>Sin diferencias en el periodo.</b> Los recibos de nómina del sistema (SAT) y la contabilidad coinciden en
          todos los meses confrontados.
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="px-4 py-2 border-b border-slate-200 bg-slate-50">
          <div className="text-sm font-semibold text-[#0F2D5C]">
            Resumen por mes — clic en una fila para ver el detalle por trabajador
          </div>
          <p className="text-[11px] text-slate-600 mt-1">
            Los meses sin diferencias muestran un aviso al expandir la fila.
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" style={{minWidth: "1000px"}}>
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Mes</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">{CF.TH_CFDI_SAT}</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Trab.</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Perc sistema (SAT)</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">ISR sistema (SAT)</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Neto sistema (SAT)</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Perc Excel (Contabilidad)</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">ISR Excel (Contabilidad)</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Δ ISR</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500">Δ Neto</th>
              <th className="w-10 border-l border-slate-200 bg-slate-50 p-0" aria-hidden />
            </tr>
          </thead>
          <tbody>
            {monthly.map(blk => {
              const isOpen = !!openMonths[blk.key];
              const t = blk.data.totales;
              const hasDiff = confrontaHasDifferences(blk.data);
              return (
                <Fragment key={blk.key}>
                  <tr className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 ${hasDiff ? "bg-amber-50/30":""}`} onClick={() => toggleMonth(blk.key)}>
                    <td className="px-3 py-2 font-medium text-slate-800">{blk.label}</td>
                    <td className="px-3 py-2 text-right num">{fmtNumber(t.sistema.count)}</td>
                    <td className="px-3 py-2 text-right num">{fmtNumber(blk.data.workers.length)}</td>
                    <td className="px-3 py-2 text-right num">{fmtMoney(t.sistema.percepciones)}</td>
                    <td className="px-3 py-2 text-right num">{fmtMoney(t.sistema.isr)}</td>
                    <td className="px-3 py-2 text-right num">{fmtMoney(t.sistema.neto)}</td>
                    <td className="px-3 py-2 text-right num">{fmtMoney(t.excel.percepciones)}</td>
                    <td className="px-3 py-2 text-right num">{fmtMoney(t.excel.isr)}</td>
                    <td className={`px-3 py-2 text-right num ${Math.abs(t.diff.isr) > 0.01 ? "text-amber-700 font-semibold":""}`}>{fmtMoney(t.diff.isr)}</td>
                    <td className={`px-3 py-2 text-right num ${Math.abs(t.diff.neto) > 0.01 ? "text-amber-700 font-semibold":""}`}>{fmtMoney(t.diff.neto)}</td>
                    <SatExpandTableRailCell open={isOpen} />
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50/50">
                      <td colSpan={11} className="p-4">
                        <div className="bg-white border border-slate-200 rounded-sm p-3">
                          {hasDiff ? (
                            <>
                              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
                                Detalle de {blk.label} · {blk.from} → {blk.to}
                              </div>
                              <NominaDetail
                                data={blk.data}
                                embedded
                                openWorkers={openWorkers}
                                setOpenWorkers={setOpenWorkers}
                                onOpenSatVisor={onOpenSatVisor}
                              />
                            </>
                          ) : (
                            <MesSinDiferenciasAviso label={blk.label} from={blk.from} to={blk.to} />
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
          <tfoot className="bg-slate-100 border-t-2 border-slate-300">
            <tr>
              <td className="px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-700">TOTAL</td>
              <td className="px-3 py-2 text-right num font-bold">{fmtNumber(agg.sistema.count)}</td>
              <td></td>
              <td className="px-3 py-2 text-right num font-bold">{fmtMoney(agg.sistema.percepciones)}</td>
              <td className="px-3 py-2 text-right num font-bold">{fmtMoney(agg.sistema.isr)}</td>
              <td className="px-3 py-2 text-right num font-bold">{fmtMoney(agg.sistema.neto)}</td>
              <td className="px-3 py-2 text-right num font-bold">{fmtMoney(agg.excel.percepciones)}</td>
              <td className="px-3 py-2 text-right num font-bold">{fmtMoney(agg.excel.isr)}</td>
              <td className="px-3 py-2 text-right num font-bold text-amber-700">{fmtMoney(agg.diff.isr)}</td>
              <td className="px-3 py-2 text-right num font-bold text-amber-700">{fmtMoney(agg.diff.neto)}</td>
              <td className="w-10 border-l border-slate-300 bg-slate-100 p-0" aria-hidden />
            </tr>
          </tfoot>
        </table>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, color, icon: Icon }) {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    red: "border-red-200 bg-red-50 text-red-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  };
  return (
    <div className={`border rounded-sm p-3 ${colors[color]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold opacity-80">
        <Icon className="w-3 h-3"/> {label}
      </div>
      <div className="num text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function ColTotals({ title, perc, isr, neto, count, highlight }) {
  const cls = highlight ? "bg-amber-50 border-amber-200" : "bg-white";
  return (
    <div className={`p-4 border-r border-slate-200 last:border-r-0 ${cls}`}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-2">{title}</div>
      <div className="space-y-1">
        <div className="flex justify-between text-xs"><span className="text-slate-500">Percepciones</span><span className="num">{fmtMoney(perc)}</span></div>
        <div className="flex justify-between text-xs"><span className="text-slate-500">ISR retenido</span><span className="num text-amber-700">{fmtMoney(isr)}</span></div>
        <div className="flex justify-between text-xs"><span className="text-slate-500">Neto</span><span className="num font-bold text-slate-900">{fmtMoney(neto)}</span></div>
        {count !== undefined && <div className="flex justify-between text-xs"><span className="text-slate-500 text-[10px]">{CF.ROW_LABEL_CFDI_SAT}</span><span className="num text-[10px]">{fmtNumber(count)}</span></div>}
      </div>
    </div>
  );
}

function SimpleTable({ rows, cols, onOpenSatVisor }) {
  if (!rows?.length) return <div className="py-10 text-center text-slate-500 text-sm">Sin registros.</div>;
  return (
    <div className="border border-slate-200 rounded-sm overflow-hidden mt-2 max-h-[480px] overflow-y-auto">
      <table className="w-full text-xs">
        <thead className="bg-slate-50 sticky top-0 z-10">
          <tr>
            {cols.map(c => (<th key={c.k} className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold text-slate-500 ${c.right?"text-right":"text-left"}`}>{c.label}</th>))}
            {onOpenSatVisor && (
              <th className="w-10 text-center text-[10px] uppercase tracking-wider font-semibold text-slate-500">Visor</th>
            )}
          </tr>
        </thead>
        <tbody>
          {rows.slice(0,300).map((r,i) => (
            <tr key={i} className="border-t border-slate-100">
              {cols.map(c => <td key={c.k} className={`px-3 py-1.5 ${c.right?"text-right num":""} ${c.mono?"font-mono text-[10px] text-slate-700":"text-slate-700"}`}>{c.render(r)}</td>)}
              {onOpenSatVisor && (
                <td className="px-2 py-1.5 text-center">
                  {r.uuid ? (
                    <button
                      type="button"
                      className="p-0.5 rounded-sm text-[#0F2D5C] hover:bg-slate-100 inline-flex"
                      title="Abrir en visor SAT (nóminas)"
                      onClick={() => onOpenSatVisor(r.uuid)}
                    >
                      <ExternalLink className="w-3.5 h-3.5"/>
                    </button>
                  ) : (
                    <span className="text-slate-300">—</span>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > 300 && <div className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-t border-slate-200 text-center">Mostrando 300 de {rows.length}.</div>}
    </div>
  );
}
